import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronUp, ChevronDown, List, Settings, AlertTriangle, MessageSquare, Users, MoreHorizontal, Plus, Mail, GripVertical, Wifi, WifiOff, X, Loader2, Search, Volume2, Mic, Bell, Bluetooth, Radio, Power, Smartphone, Monitor, Moon, BatteryCharging, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAudioConnection } from '../../context/AudioConnectionContext';
import { useSignalingContext } from '../../context/SignalingContext';
import { useMobileRadioContext } from '../../context/MobileRadioContext';
import { PTT_STATES } from '../../constants/pttStates';
import { updateUnitStatus } from '../../utils/api';
import { startBackgroundService, stopBackgroundService } from '../../plugins/backgroundService';
import { updateServiceConnectionInfo } from '../../plugins/nativeAudioBridge';
import { setupAppLifecycle, getSettings, saveSettings, isNative, setHardwarePttKeyCode } from '../../lib/capacitor';
import { signalingManager } from '../../signaling/SignalingManager';
import { useClearAir } from './useClearAir';
import { startTracking as gpsStartTracking, stopTracking as gpsStopTracking } from '../../services/gpsService.js';
import { PTTButton } from './PTTButton';
import { getSharedAudioContext } from '../../audio/iosAudioUnlock';
import { 
  AnimalSearchModal, 
  CitationModal, 
  CadMapModal, 
  FieldInterviewModal, 
  FleetModal, 
  WeatherWidget, 
  BoloWidget,
  MessagesWidget,
  NewMessageModal 
} from './widgets';

function checkIsT320() {
  if (typeof window === 'undefined') return false;
  var iw = window.innerWidth || 0;
  var sw = window.screen ? window.screen.width : 0;
  var isCapacitor = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  if (iw <= 280 || sw <= 280) return true;
  if (isCapacitor && (iw <= 480 || sw <= 480)) return true;
  return false;
}

function useIsT320() {
  var val = useState(checkIsT320);
  var isT320 = val[0];
  var setIsT320 = val[1];
  useEffect(function() {
    var handler = function() { setIsT320(checkIsT320()); };
    window.addEventListener('resize', handler);
    return function() { window.removeEventListener('resize', handler); };
  }, []);
  return isT320;
}

const STATUS_LABELS = {
  'off_duty': 'OFF DUTY',
  'on_duty': 'ON DUTY',
  'en_route': 'EN ROUTE',
  'arrived': 'ARRIVED',
  'oos': 'OOS',
  'out_of_service': 'OOS',
};

const STATUS_CYCLE = ['off_duty', 'on_duty', 'en_route', 'arrived', 'oos'];

function formatStatus(status) {
  return STATUS_LABELS[status?.toLowerCase()] || status?.toUpperCase()?.replace(/_/g, ' ') || 'UNKNOWN';
}

function getNextStatus(currentStatus) {
  const currentIndex = STATUS_CYCLE.indexOf(currentStatus?.toLowerCase());
  const nextIndex = (currentIndex + 1) % STATUS_CYCLE.length;
  return STATUS_CYCLE[nextIndex];
}

const defaultButtons = [
  { id: 'f1', label: 'Person', sublabel: 'Query' },
  { id: 'f2', label: 'Vehicle', sublabel: 'Query' },
  { id: 'f3', label: 'Custom', sublabel: 'F3' },
  { id: 'f4', label: 'Custom', sublabel: 'F4' },
  { id: 'widget', label: 'Widget', sublabel: '' },
];

const F3F4_WIDGET_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'animal', label: 'Animal Search' },
  { value: 'citation', label: 'Citation/Warning' },
  { value: 'map', label: 'CAD Map' },
  { value: 'fi', label: 'Field Interview' },
  { value: 'fleet', label: 'Fleet' },
];

const MAIN_WIDGET_OPTIONS = [
  { value: 'weather', label: 'Weather' },
  { value: 'bolo', label: 'Recent BOLOs' },
];

export function RadioDeckView({ user, onLogout }) {
  const identity = (user?.unit_id && user.unit_id.trim()) || user?.username || 'Unknown';
  
  const {
    audioTransportManager,
    connectionStatus,
    switchChannel: contextSwitchChannel,
    ensureConnected,
  } = useAudioConnection();
  
  const {
    channelMembers,
    activeTransmissions,
    isTransmitting: isChannelTransmitting,
    getTransmittingUnit,
    joinChannel: signalingJoinChannel,
    leaveChannel: signalingLeaveChannel,
    signalPttStart,
    signalPttEnd,
    signalEmergencyStart,
    signalEmergencyEnd,
    connected: signalingConnected,
    emergencyAlerts,
  } = useSignalingContext();
  
  const { isScanning, toggleScanning, isEmergency, setIsEmergency, triggerEmergency, cancelEmergency, scanChannels, setScanChannels, toggleScanChannel } = useMobileRadioContext();
  
  const connected = signalingConnected;
  const connecting = connectionStatus === 'connecting';
  
  const [channels, setChannels] = useState([]);

  const { clearAirActive, clearAirChannelName } = useClearAir({
    identity,
    channels,
    isScanning,
    scanChannels,
  });
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [currentChannelIndex, setCurrentChannelIndex] = useState(0);
  const [currentZoneIndex, setCurrentZoneIndex] = useState(0);
  const [showPTT, setShowPTT] = useState(() => {
    const saved = localStorage.getItem('radio_show_ptt');
    return saved !== 'false';
  });
  const [showScanList, setShowScanList] = useState(false);
  const [showT320Menu, setShowT320Menu] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [unitStatus, setUnitStatus] = useState('off_duty');
  const [hasActiveCall, setHasActiveCall] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [cadConnected, setCadConnected] = useState(null);
  const [showPersonQuery, setShowPersonQuery] = useState(false);
  const [showVehicleQuery, setShowVehicleQuery] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [showAnimalSearch, setShowAnimalSearch] = useState(false);
  const [showCitation, setShowCitation] = useState(false);
  const [showCadMap, setShowCadMap] = useState(false);
  const [showFI, setShowFI] = useState(false);
  const [showFleet, setShowFleet] = useState(false);
  const [showWeather, setShowWeather] = useState(false);
  const [showBolo, setShowBolo] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [appSettings, setAppSettings] = useState(null);
  const [batteryOptExempt, setBatteryOptExempt] = useState(null);
  const [capturingPttKey, setCapturingPttKey] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [personQueryForm, setPersonQueryForm] = useState({ firstName: '', lastName: '', dob: '' });
  const [vehicleQueryForm, setVehicleQueryForm] = useState({ plate: '', state: '', vin: '' });
  const [queryResult, setQueryResult] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState(null);
  const [functionButtons, setFunctionButtons] = useState(() => {
    const saved = localStorage.getItem('radio_function_buttons');
    return saved ? JSON.parse(saved) : defaultButtons;
  });
  const [widgetConfig, setWidgetConfig] = useState(() => {
    const saved = localStorage.getItem('radio_widget_config');
    return saved ? JSON.parse(saved) : { f3: 'none', f4: 'none', widget: 'weather' };
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [isTransmitting, setIsTransmitting] = useState(false);
  const [pttState, setPttState] = useState(PTT_STATES.IDLE);
  const [activeAudio, setActiveAudio] = useState(null);
  const [keysLocked, setKeysLocked] = useState(false);
  const [emergencyHoldProgress, setEmergencyHoldProgress] = useState(null);
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [clockTime, setClockTime] = useState('');
  
  const hasJoinedRef = useRef(false);
  const transmitChannelRef = useRef('');
  const isEmergencyRef = useRef(false);
  const audioUrlRef = useRef('');
  const rxAudioElementsRef = useRef(new Set());
  const emergencyAlarmRef = useRef(null);

  useEffect(() => {
    if (emergencyAlerts && emergencyAlerts.length > 0 && !isEmergency && !emergencyAlarmRef.current) {
      try {
        var ctx = getSharedAudioContext();
        var gain = ctx.createGain();
        gain.gain.value = 0.5;
        gain.connect(ctx.destination);
        var osc1 = ctx.createOscillator();
        osc1.type = 'square';
        osc1.frequency.value = 1200;
        var g1 = ctx.createGain();
        g1.gain.value = 0;
        osc1.connect(g1);
        g1.connect(gain);
        var osc2 = ctx.createOscillator();
        osc2.type = 'square';
        osc2.frequency.value = 1800;
        var g2 = ctx.createGain();
        g2.gain.value = 0;
        osc2.connect(g2);
        g2.connect(gain);
        osc1.start();
        osc2.start();
        var toggle = true;
        var iv = setInterval(function() {
          g1.gain.value = toggle ? 0.5 : 0;
          g2.gain.value = toggle ? 0 : 0.5;
          toggle = !toggle;
        }, 500);
        emergencyAlarmRef.current = function() {
          clearInterval(iv);
          // AudioContext is shared (see iosAudioUnlock.getSharedAudioContext) — do not close it here.
          try { osc1.stop(); osc2.stop(); } catch(e) {}
        };
        setTimeout(function() {
          if (emergencyAlarmRef.current) {
            emergencyAlarmRef.current();
            emergencyAlarmRef.current = null;
          }
        }, 10000);
      } catch(e) {}
    } else if ((!emergencyAlerts || emergencyAlerts.length === 0) && emergencyAlarmRef.current) {
      emergencyAlarmRef.current();
      emergencyAlarmRef.current = null;
    }
    return () => {
      if (emergencyAlarmRef.current) {
        emergencyAlarmRef.current();
        emergencyAlarmRef.current = null;
      }
    };
  }, [emergencyAlerts, isEmergency]);

  useEffect(() => {
    const removeTrackStartListener = signalingManager.on('location:track_start', () => {
      console.log('[RadioDeck] GPS track_start received');
      gpsStartTracking();
    });

    const removeTrackStopListener = signalingManager.on('location:track_stop', () => {
      console.log('[RadioDeck] GPS track_stop received');
      gpsStopTracking();
    });

    return () => {
      removeTrackStartListener();
      removeTrackStopListener();
      gpsStopTracking();
    };
  }, []);


  useEffect(() => {
    startBackgroundService().then(result => {
      if (result.success) {
        console.log('[PTT-DIAG] [JS] Background service started');
      }
    }).catch(err => {
      console.warn('[PTT-DIAG] [JS] Failed to start background service:', err);
    });

    const serverBaseUrl = window.location.origin;
    const txChannel = transmitChannelRef.current;
    fetch('/api/livekit-url')
      .then(r => r.json())
      .then(data => {
        const audioUrl = data.url || '';
        audioUrlRef.current = audioUrl;
        console.log('[PTT-DIAG] [JS] Passing early connection info to service: unit=' + identity + ' channel=' + txChannel + ' server=' + serverBaseUrl + ' lkUrl=' + audioUrl);
        updateServiceConnectionInfo(serverBaseUrl, identity, txChannel || '', audioUrl, txChannel || '');
      })
      .catch(err => {
        console.warn('[PTT-DIAG] [JS] Failed to fetch livekit-url, passing without lkUrl:', err);
        updateServiceConnectionInfo(serverBaseUrl, identity, txChannel || '', '', txChannel || '');
      });
    
    return () => {
      stopBackgroundService().catch(err => {
        console.warn('[PTT-DIAG] [JS] Failed to stop background service:', err);
      });
    };
  }, []);

  useEffect(() => {
    const cleanup = setupAppLifecycle(
      () => {
        console.log('[RadioDeck] App resumed — verifying connections');
        signalingManager.verifyConnection().then(ok => {
          if (ok) {
            console.log('[RadioDeck] Signaling connection verified');
          } else {
            console.warn('[RadioDeck] Signaling connection could not be restored');
          }
        });
        try {
          // Shared AudioContext is the single resume entrypoint — every audio engine and
          // tone helper uses getSharedAudioContext(), so this one call covers all of them.
          const ac = getSharedAudioContext();
          if (ac && ac.state === 'suspended') {
            ac.resume().then(() => console.log('[RadioDeck] Shared AudioContext resumed'));
          }
        } catch (e) {
          console.warn('[RadioDeck] AudioContext resume failed:', e.message);
        }

        if (window.__audioTransportManager) {
          const mgr = window.__audioTransportManager;
          const connectedChannels = mgr.getConnectedChannels();
          for (const ch of connectedChannels) {
            const conn = mgr.getRoom(ch);
            if (conn && conn.ws && conn.ws.readyState === WebSocket.OPEN) {
              console.log(`[RadioDeck] Audio WS for ${ch} still open`);
              continue;
            }
            const savedUnitId = conn ? conn.unitId : '';
            console.log(`[RadioDeck] Audio WS dead for ${ch} after resume — reconnecting`);
            mgr.disconnect(ch).then(() => {
              mgr.connect(ch, savedUnitId);
            });
          }
        }
      },
      () => {
        console.log('[RadioDeck] App paused — keeping connections alive');
      }
    );
    return cleanup;
  }, []);

  useEffect(() => {
    fetch('/api/channels', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        const channelList = Array.isArray(data) ? data : (data?.channels || []);
        if (channelList.length > 0) {
          setChannels(channelList);
          setScanChannels(prev => {
            const savedMap = {};
            prev.forEach(function(ch) { savedMap[ch.id] = ch.enabled; });
            return channelList.map(function(ch) {
              return {
                id: ch.id,
                name: ch.name,
                enabled: savedMap[ch.id] !== undefined ? savedMap[ch.id] : (prev.length === 0),
              };
            });
          });
        }
        setChannelsLoading(false);
      })
      .catch(err => {
        console.error('Failed to load channels:', err);
        setChannelsLoading(false);
      });
  }, [setScanChannels]);

  const zones = useMemo(() => {
    const zoneSet = new Set();
    channels.forEach(ch => {
      if (ch.zone) zoneSet.add(ch.zone);
    });
    const uniqueZones = Array.from(zoneSet).sort();
    return uniqueZones;
  }, [channels]);
  
  const currentZone = zones[currentZoneIndex] || (zones.length > 0 ? zones[0] : '');
  
  const filteredChannels = useMemo(() => {
    return channels.filter(ch => ch.zone === currentZone);
  }, [channels, currentZone]);
  
  const currentChannel = filteredChannels[currentChannelIndex];
  const currentChannelName = currentChannel?.name || null;
  const currentRoomKey = currentChannel?.room_key || (currentChannel ? ((currentChannel.zone || 'Default') + '__' + currentChannel.name) : null);

  useEffect(() => {
    transmitChannelRef.current = currentRoomKey || '';
    if (currentRoomKey && identity) {
      const serverBaseUrl = window.location.origin;
      const audioUrl = audioUrlRef.current;
      console.log('[PTT-DIAG] [JS] Channel changed — updating service connection info: channel=' + currentRoomKey + ' lkUrl=' + audioUrl);
      updateServiceConnectionInfo(serverBaseUrl, identity, currentRoomKey, audioUrl, currentRoomKey);
    }
  }, [currentRoomKey, identity]);

  
  useEffect(() => {
    isEmergencyRef.current = isEmergency;
  }, [isEmergency]);

  useEffect(() => {
    if (currentChannel && currentRoomKey && !hasJoinedRef.current) {
      hasJoinedRef.current = true;
      contextSwitchChannel(currentRoomKey, identity);
      signalingJoinChannel(currentRoomKey);
    }
  }, [currentChannel, currentRoomKey, contextSwitchChannel, signalingJoinChannel, identity]);


  useEffect(() => {
    if (!audioTransportManager) return;
    audioTransportManager.setAutoPlayback(false);
    
    const listenerRemovers = [];
    
    listenerRemovers.push(
      audioTransportManager.addTrackSubscribedListener((channelName, track, participant) => {
        if (track.kind !== 'audio') return;
        
        const audioElem = track.attach();
        audioElem.dataset.channel = channelName;
        audioElem.dataset.participant = participant.identity;
        audioElem.playsInline = true;
        audioElem.autoplay = true;
        audioElem.style.display = 'none';
        
        document.body.appendChild(audioElem);
        rxAudioElementsRef.current.add(audioElem);
        
        const currentState = audioTransportManager.getState();
        if (currentState === PTT_STATES.TRANSMITTING || currentState === PTT_STATES.ARMING) {
          audioElem.muted = true;
        } else {
          audioElem.muted = false;
          audioElem.volume = 1.0;
        }
        
        audioElem.play().catch(() => {});
        setActiveAudio({ channel: channelName, from: participant.identity });
      })
    );
    
    listenerRemovers.push(
      audioTransportManager.addTrackUnsubscribedListener((channelName, track, participant) => {
        const detachedElements = track.detach();
        detachedElements.forEach((el) => {
          rxAudioElementsRef.current.delete(el);
          el.remove();
        });
        setActiveAudio(null);
      })
    );
    
    return () => {
      if (audioTransportManager) {
        audioTransportManager.setAutoPlayback(true);
      }
      listenerRemovers.forEach(remove => remove());
    };
  }, [audioTransportManager]);

  const getRoomKey = useCallback((ch) => {
    if (!ch) return null;
    return ch.room_key || ((ch.zone || 'Default') + '__' + ch.name);
  }, []);

  const broadcastStatus = useCallback((status, channel) => {
    if (!audioTransportManager?.isConnected(channel)) return;
    
    audioTransportManager.sendData(channel, {
      type: 'status_update',
      identity,
      status,
      channel,
      timestamp: Date.now(),
    });
  }, [audioTransportManager, identity]);

  useEffect(() => {
    audioTransportManager.onStateChange = (newState) => {
      setPttState(newState);
      const txChannel = transmitChannelRef.current;
      
      if (newState === PTT_STATES.ARMING) {
        rxAudioElementsRef.current.forEach(el => { el.muted = true; });
      } else if (newState === PTT_STATES.TRANSMITTING) {
        setIsTransmitting(true);
        rxAudioElementsRef.current.forEach(el => { el.muted = true; });
        if (txChannel) {
          broadcastStatus('transmitting', txChannel);
          updateUnitStatus(identity, txChannel, 'transmitting', null, isEmergencyRef.current).catch(() => {});
        }
      } else if (newState === PTT_STATES.IDLE) {
        setIsTransmitting(false);
        rxAudioElementsRef.current.forEach(el => { el.muted = false; });
        if (txChannel) {
          broadcastStatus('idle', txChannel);
          updateUnitStatus(identity, txChannel, 'idle', null, isEmergencyRef.current).catch(() => {});
        }
      }
    };
    
    return () => {
      audioTransportManager.disconnect();
    };
  }, [broadcastStatus, identity]);

  useEffect(() => {
    const fetchInitialStatus = async () => {
      try {
        const response = await fetch('/api/cad/status-check', { credentials: 'include' });
        const data = await response.json();
        
        if (response.ok && data.success && data.units && Array.isArray(data.units)) {
          setCadConnected(true);
          const myUnit = data.units.find(u => u.unit_id === identity);
          if (myUnit?.status) {
            console.log('[Status] Initial status from CAD:', myUnit.status);
            setUnitStatus(myUnit.status);
          }
        } else {
          console.log('[Status] CAD not connected');
          setCadConnected(false);
        }
      } catch (err) {
        console.error('[Status] Failed to fetch initial status:', err);
        setCadConnected(false);
      }
    };
    
    if (identity) {
      fetchInitialStatus();
    }
  }, [identity]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await fetch('/api/cad/messages/unread', { credentials: 'include' });
        const data = await response.json();
        if (response.ok && typeof data.count === 'number') {
          setUnreadCount(data.count);
        }
      } catch (err) {
        console.error('[Messages] Failed to fetch unread count:', err);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCycleStatus = async () => {
    if (statusLoading) return;
    setStatusLoading(true);
    
    const nextStatus = getNextStatus(unitStatus);
    
    try {
      const response = await fetch(`/api/cad/unit/${encodeURIComponent(identity)}/status/cycle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (response.ok && data.success !== false) {
        const newStatus = data.newStatus || data.status;
        if (newStatus) {
          setUnitStatus(newStatus);
          setHasActiveCall(data.hasActiveCall || false);
          console.log('[Status] Cycled to:', newStatus, 'hasActiveCall:', data.hasActiveCall);
          if (currentRoomKey) {
            broadcastStatus(newStatus, currentRoomKey);
          }
        } else {
          setUnitStatus(nextStatus);
          if (currentRoomKey) {
            broadcastStatus(nextStatus, currentRoomKey);
          }
        }
      } else {
        console.log('[Status] CAD unavailable, using local cycle:', nextStatus);
        setUnitStatus(nextStatus);
        if (currentRoomKey) {
          broadcastStatus(nextStatus, currentRoomKey);
        }
      }
    } catch (err) {
      console.error('Failed to cycle status, using local fallback:', err);
      setUnitStatus(nextStatus);
      if (currentRoomKey) {
        broadcastStatus(nextStatus, currentRoomKey);
      }
    } finally {
      setStatusLoading(false);
    }
  };
  
  const handleZoneUp = () => {
    const oldChannel = filteredChannels[currentChannelIndex];
    if (oldChannel) {
      signalingLeaveChannel(getRoomKey(oldChannel));
    }
    
    setCurrentZoneIndex(prev => (prev + 1) % zones.length);
    setCurrentChannelIndex(0);
    hasJoinedRef.current = false;
  };
  
  const handleZoneDown = () => {
    const oldChannel = filteredChannels[currentChannelIndex];
    if (oldChannel) {
      signalingLeaveChannel(getRoomKey(oldChannel));
    }
    
    setCurrentZoneIndex(prev => prev === 0 ? zones.length - 1 : prev - 1);
    setCurrentChannelIndex(0);
    hasJoinedRef.current = false;
  };

  useEffect(() => {
    if (hasJoinedRef.current === false && filteredChannels.length > 0 && currentChannelIndex < filteredChannels.length) {
      const channel = filteredChannels[currentChannelIndex];
      if (channel) {
        hasJoinedRef.current = true;
        const rk = getRoomKey(channel);
        contextSwitchChannel(rk, identity);
        signalingJoinChannel(rk);
      }
    }
  }, [currentZoneIndex, filteredChannels, currentChannelIndex, contextSwitchChannel, signalingJoinChannel, identity, getRoomKey]);

  const handleChannelUp = () => {
    if (filteredChannels.length === 0) return;
    const oldChannel = filteredChannels[currentChannelIndex];
    if (oldChannel) {
      signalingLeaveChannel(getRoomKey(oldChannel));
    }
    
    const newIndex = (currentChannelIndex + 1) % filteredChannels.length;
    setCurrentChannelIndex(newIndex);
    const channel = filteredChannels[newIndex];
    if (channel) {
      const rk = getRoomKey(channel);
      contextSwitchChannel(rk, identity);
      signalingJoinChannel(rk);
    }
  };

  const handleChannelDown = () => {
    if (filteredChannels.length === 0) return;
    const oldChannel = filteredChannels[currentChannelIndex];
    if (oldChannel) {
      signalingLeaveChannel(getRoomKey(oldChannel));
    }
    
    const newIndex = currentChannelIndex === 0 ? filteredChannels.length - 1 : currentChannelIndex - 1;
    setCurrentChannelIndex(newIndex);
    const channel = filteredChannels[newIndex];
    if (channel) {
      const rk = getRoomKey(channel);
      contextSwitchChannel(rk, identity);
      signalingJoinChannel(rk);
    }
  };

  const handleEmergencyActivate = async () => {
    if (isEmergency) return;
    const channel = currentRoomKey || 'DISPATCH';
    try {
      await triggerEmergency();
    } catch (err) {
      console.error('[Emergency] triggerEmergency failed:', err);
    }
    try {
      gpsStartTracking();
    } catch (err) {
      console.error('[Emergency] GPS start failed:', err);
    }
    try {
      signalEmergencyStart(channel);
    } catch (err) {
      console.error('[Emergency] Signaling broadcast failed:', err);
    }
    try {
      await fetch('/api/dispatch/emergency', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ channel, active: true, unitId: identity }),
      });
    } catch (err) {
      console.error('[Emergency] Failed to notify dispatcher:', err);
    }
  };

  const handleEmergencyDeactivate = async () => {
    if (!isEmergency) return;
    const channel = currentRoomKey || 'DISPATCH';
    try {
      await cancelEmergency();
    } catch (err) {
      console.error('[Emergency] cancelEmergency failed:', err);
      setIsEmergency(false);
    }
    try {
      gpsStopTracking();
    } catch (err) {
      console.error('[Emergency] GPS stop failed:', err);
    }
    try {
      signalEmergencyEnd(channel);
    } catch (err) {
      console.error('[Emergency] Signaling end broadcast failed:', err);
    }
    try {
      await fetch('/api/dispatch/emergency', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ channel, active: false, unitId: identity }),
      });
    } catch (err) {
      console.error('[Emergency] Failed to notify dispatcher of cancellation:', err);
    }
  };

  const handleEmergencyActivateRef = useRef(handleEmergencyActivate);
  const handleEmergencyDeactivateRef = useRef(handleEmergencyDeactivate);
  useEffect(() => {
    handleEmergencyActivateRef.current = handleEmergencyActivate;
    handleEmergencyDeactivateRef.current = handleEmergencyDeactivate;
  }, [isEmergency, currentRoomKey, identity]);

  const handleTransmitStart = useCallback(async () => {
    const channelName = transmitChannelRef.current;
    console.log('[PTT-DIAG] [JS] handleTransmitStart() — channel=' + channelName + ' identity=' + identity);
    if (!channelName) {
      console.warn('[PTT-DIAG] [JS] handleTransmitStart() — NO CHANNEL, aborting');
      return;
    }
    
    const isConn = await ensureConnected(channelName);
    console.log('[PTT-DIAG] [JS] handleTransmitStart() — ensureConnected=' + isConn);
    if (!isConn) {
      console.warn('[PTT-DIAG] [JS] handleTransmitStart() — ensureConnected FAILED for', channelName);
    }
    const room = audioTransportManager?.getRoom(channelName);
    console.log('[PTT-DIAG] [JS] handleTransmitStart() — room=' + (room ? (room._isNativeProxy ? 'NATIVE_PROXY' : 'WEB_SDK') : 'NULL') + ' state=' + (room?.state || 'N/A'));
    if (!room) {
      console.warn('[PTT-DIAG] [JS] handleTransmitStart() — NO ROOM, aborting');
      return;
    }
    
    audioTransportManager.setCurrentChannel(channelName);
    audioTransportManager.setCurrentUnit(identity);
    audioTransportManager.setRoom(room);
    try {
      await signalPttStart(channelName);
    } catch (grantErr) {
      console.warn('[PTT-DIAG] [JS] Floor denied:', grantErr.message);
      return;
    }
    console.log('[PTT-DIAG] [JS] handleTransmitStart() — calling audioTransportManager.start()');
    audioTransportManager.start();
  }, [ensureConnected, audioTransportManager, signalPttStart, identity]);

  const handleTransmitEnd = useCallback(() => {
    const channelName = transmitChannelRef.current;
    console.log('[PTT-DIAG] [JS] handleTransmitEnd() — channel=' + channelName + ' canStop=' + audioTransportManager.canStop());
    if (audioTransportManager.canStop()) {
      audioTransportManager.stop();
      if (channelName) {
        signalPttEnd(channelName);
      }
    }
  }, [signalPttEnd]);

  const handleDragStart = (id) => {
    setDraggedItem(id);
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;
    
    const newButtons = [...functionButtons];
    const draggedIndex = newButtons.findIndex(b => b.id === draggedItem);
    const targetIndex = newButtons.findIndex(b => b.id === targetId);
    
    if (draggedIndex !== -1 && targetIndex !== -1) {
      const [removed] = newButtons.splice(draggedIndex, 1);
      newButtons.splice(targetIndex, 0, removed);
      setFunctionButtons(newButtons);
      localStorage.setItem('radio_function_buttons', JSON.stringify(newButtons));
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handlePersonQuery = async () => {
    if (!personQueryForm.firstName && !personQueryForm.lastName && !personQueryForm.dob) {
      setQueryError('Please enter at least one search field');
      return;
    }
    setQueryLoading(true);
    setQueryError(null);
    setQueryResult(null);
    try {
      const response = await fetch('/api/cad/query/person', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(personQueryForm),
      });
      const data = await response.json();
      if (response.ok) {
        setQueryResult(data);
      } else {
        setQueryError(data.message || 'Query failed');
      }
    } catch (err) {
      setQueryError('Failed to connect to CAD');
    } finally {
      setQueryLoading(false);
    }
  };

  const handleVehicleQuery = async () => {
    if (!vehicleQueryForm.plate && !vehicleQueryForm.vin) {
      setQueryError('Please enter plate or VIN');
      return;
    }
    setQueryLoading(true);
    setQueryError(null);
    setQueryResult(null);
    try {
      const response = await fetch('/api/cad/query/vehicle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(vehicleQueryForm),
      });
      const data = await response.json();
      if (response.ok) {
        setQueryResult(data);
      } else {
        setQueryError(data.message || 'Query failed');
      }
    } catch (err) {
      setQueryError('Failed to connect to CAD');
    } finally {
      setQueryLoading(false);
    }
  };

  const fetchContacts = async () => {
    setContactsLoading(true);
    try {
      const response = await fetch('/api/unit/contacts', { credentials: 'include' });
      const data = await response.json();
      if (Array.isArray(data)) {
        setContacts(data);
      } else if (data.contacts) {
        setContacts(data.contacts);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleOpenContacts = () => {
    setShowContacts(true);
    fetchContacts();
  };

  const openWidgetByType = (widgetType) => {
    switch (widgetType) {
      case 'animal':
        setShowAnimalSearch(true);
        break;
      case 'citation':
        setShowCitation(true);
        break;
      case 'map':
        setShowCadMap(true);
        break;
      case 'fi':
        setShowFI(true);
        break;
      case 'fleet':
        setShowFleet(true);
        break;
      case 'weather':
        setShowWeather(true);
        break;
      case 'bolo':
        setShowBolo(true);
        break;
      default:
        break;
    }
  };

  const handleFunctionButtonClick = (btnId) => {
    if (btnId === 'f1') {
      setQueryResult(null);
      setQueryError(null);
      setShowPersonQuery(true);
    } else if (btnId === 'f2') {
      setQueryResult(null);
      setQueryError(null);
      setShowVehicleQuery(true);
    } else if (btnId === 'f3') {
      openWidgetByType(widgetConfig.f3);
    } else if (btnId === 'f4') {
      openWidgetByType(widgetConfig.f4);
    } else if (btnId === 'widget') {
      openWidgetByType(widgetConfig.widget);
    }
  };

  useEffect(() => {
    getSettings().then(s => setAppSettings(s));
    if (isNative && showSettings && window.Capacitor?.Plugins?.BackgroundService?.checkBatteryOptimization) {
      window.Capacitor.Plugins.BackgroundService.checkBatteryOptimization()
        .then(r => setBatteryOptExempt(r?.isExempt ?? null))
        .catch(() => setBatteryOptExempt(null));
    }
  }, [showSettings]);

  useEffect(() => {
    if (!capturingPttKey) return;
    const handleKeyDown = async (e) => {
      e.preventDefault();
      if (appSettings) {
        const newSettings = {
          ...appSettings,
          pttKeyCode: e.keyCode,
          pttKeyLabel: e.key || `Key ${e.keyCode}`,
        };
        setAppSettings(newSettings);
        await saveSettings(newSettings);
        await setHardwarePttKeyCode(e.keyCode);
      }
      setCapturingPttKey(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [capturingPttKey, appSettings]);

  const updateAppSetting = async (key, value) => {
    if (!appSettings) return;
    const newSettings = { ...appSettings, [key]: value };
    setAppSettings(newSettings);
    await saveSettings(newSettings);
  };

  const clearPttKeyMapping = () => {
    if (!appSettings) return;
    const newSettings = { ...appSettings, pttKeyCode: null, pttKeyLabel: 'Screen Button' };
    setAppSettings(newSettings);
    saveSettings(newSettings);
  };

  const togglePTTVisibility = () => {
    const newValue = !showPTT;
    setShowPTT(newValue);
    localStorage.setItem('radio_show_ptt', String(newValue));
  };

  const handleWidgetConfigChange = (key, value) => {
    const newConfig = { ...widgetConfig, [key]: value };
    setWidgetConfig(newConfig);
    localStorage.setItem('radio_widget_config', JSON.stringify(newConfig));
  };

  const getButtonDisplay = (btnId) => {
    if (btnId === 'f1') return { label: 'Person', sublabel: 'Query' };
    if (btnId === 'f2') return { label: 'Vehicle', sublabel: 'Query' };
    if (btnId === 'f3') {
      const opt = F3F4_WIDGET_OPTIONS.find(o => o.value === widgetConfig.f3);
      return { label: opt?.label || 'F3', sublabel: 'F3' };
    }
    if (btnId === 'f4') {
      const opt = F3F4_WIDGET_OPTIONS.find(o => o.value === widgetConfig.f4);
      return { label: opt?.label || 'F4', sublabel: 'F4' };
    }
    if (btnId === 'widget') {
      const opt = MAIN_WIDGET_OPTIONS.find(o => o.value === widgetConfig.widget);
      return { label: opt?.label || 'Widget', sublabel: '' };
    }
    return { label: btnId, sublabel: '' };
  };

  const isReceiving = !!activeAudio;
  const transmittingUnitId = useMemo(() => {
    if (!currentRoomKey) return null;
    return getTransmittingUnit(currentRoomKey);
  }, [currentRoomKey, getTransmittingUnit, activeTransmissions]);

  const isT320 = useIsT320();

  var handleTransmitStartRef = useRef(handleTransmitStart);
  var handleTransmitEndRef = useRef(handleTransmitEnd);
  var handleChannelUpRef = useRef(handleChannelUp);
  var handleChannelDownRef = useRef(handleChannelDown);
  var handleZoneUpRef = useRef(handleZoneUp);
  var handleZoneDownRef = useRef(handleZoneDown);
  var toggleScanningRef = useRef(toggleScanning);
  useEffect(function() {
    handleTransmitStartRef.current = handleTransmitStart;
    handleTransmitEndRef.current = handleTransmitEnd;
    handleChannelUpRef.current = handleChannelUp;
    handleChannelDownRef.current = handleChannelDown;
    handleZoneUpRef.current = handleZoneUp;
    handleZoneDownRef.current = handleZoneDown;
    toggleScanningRef.current = toggleScanning;
  }, [handleTransmitStart, handleTransmitEnd, handleChannelUp, handleChannelDown, handleZoneUp, handleZoneDown, toggleScanning]);

  var keysLockedRef = useRef(false);
  useEffect(function() { keysLockedRef.current = keysLocked; }, [keysLocked]);

  useEffect(function() {
    var pttDown = false;
    var accDown = false;
    var emergencyDown = false;
    var KEY_PTT = 230;
    var KEY_ACC = 231;
    var KEY_EMERGENCY = 233;
    var KEY_DPAD_UP = 19;
    var KEY_DPAD_DOWN = 20;
    var KEY_DPAD_LEFT = 21;
    var KEY_DPAD_RIGHT = 22;
    var KEY_STAR = 17;

    var starDownTime = 0;
    var starLongPressTimer = null;
    var LONG_PRESS_MS = 1000;

    var emergencyDownTime = 0;
    var emergencyLongPressTimer = null;
    var emergencyProgressInterval = null;
    var EMERGENCY_ACTIVATE_MS = 2000;
    var EMERGENCY_DEACTIVATE_MS = 3000;

    function clearEmergencyTimers() {
      if (emergencyLongPressTimer) { clearTimeout(emergencyLongPressTimer); emergencyLongPressTimer = null; }
      if (emergencyProgressInterval) { clearInterval(emergencyProgressInterval); emergencyProgressInterval = null; }
      emergencyDownTime = 0;
      setEmergencyHoldProgress(null);
    }

    var onKeyDown = function(e) {
      var code = e.keyCode || e.which;

      if (code === KEY_STAR) {
        e.preventDefault();
        e.stopPropagation();
        if (!starDownTime) {
          starDownTime = Date.now();
          starLongPressTimer = setTimeout(function() {
            setKeysLocked(function(prev) {
              var next = !prev;
              keysLockedRef.current = next;
              return next;
            });
          }, LONG_PRESS_MS);
        }
        return;
      }

      if (keysLockedRef.current) return;

      if (code === KEY_PTT && !pttDown) {
        e.preventDefault();
        e.stopPropagation();
        pttDown = true;
        console.log('[PTT-DIAG] [JS] keydown PTT (230) — calling handleTransmitStart');
        handleTransmitStartRef.current();
      } else if (code === KEY_DPAD_UP) {
        e.preventDefault();
        e.stopPropagation();
        handleChannelUpRef.current();
      } else if (code === KEY_DPAD_DOWN) {
        e.preventDefault();
        e.stopPropagation();
        handleChannelDownRef.current();
      } else if (code === KEY_DPAD_LEFT) {
        e.preventDefault();
        e.stopPropagation();
        handleZoneDownRef.current();
      } else if (code === KEY_DPAD_RIGHT) {
        e.preventDefault();
        e.stopPropagation();
        handleZoneUpRef.current();
      } else if (code === KEY_EMERGENCY) {
        e.preventDefault();
        e.stopPropagation();
        if (!emergencyDown) {
          emergencyDown = true;
          emergencyDownTime = Date.now();
          var holdMs = isEmergencyRef.current ? EMERGENCY_DEACTIVATE_MS : EMERGENCY_ACTIVATE_MS;
          var label = isEmergencyRef.current ? 'CANCEL' : 'EMERG';
          setEmergencyHoldProgress({ label: label, percent: 0 });
          emergencyProgressInterval = setInterval(function() {
            var elapsed = Date.now() - emergencyDownTime;
            var pct = Math.min(100, Math.round((elapsed / holdMs) * 100));
            setEmergencyHoldProgress({ label: label, percent: pct });
          }, 100);
          emergencyLongPressTimer = setTimeout(function() {
            clearEmergencyTimers();
            if (isEmergencyRef.current) {
              handleEmergencyDeactivateRef.current();
            } else {
              handleEmergencyActivateRef.current();
            }
          }, holdMs);
        }
      } else if (code === KEY_ACC) {
        e.preventDefault();
        e.stopPropagation();
        if (!accDown) {
          accDown = true;
          toggleScanningRef.current();
        }
      }
    };

    var onKeyUp = function(e) {
      var code = e.keyCode || e.which;

      if (code === KEY_STAR) {
        e.preventDefault();
        e.stopPropagation();
        if (starLongPressTimer) {
          clearTimeout(starLongPressTimer);
          starLongPressTimer = null;
        }
        starDownTime = 0;
        return;
      }

      if (code === KEY_EMERGENCY) {
        e.preventDefault();
        e.stopPropagation();
        emergencyDown = false;
        clearEmergencyTimers();
        return;
      }

      if (code === KEY_ACC) {
        e.preventDefault();
        e.stopPropagation();
        accDown = false;
        return;
      }

      if (keysLockedRef.current) return;

      if (code === KEY_PTT && pttDown) {
        e.preventDefault();
        e.stopPropagation();
        pttDown = false;
        console.log('[PTT-DIAG] [JS] keyup PTT (230) — calling handleTransmitEnd');
        handleTransmitEndRef.current();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keyup', onKeyUp, true);

    var capCleanup = null;
    if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.HardwarePtt) {
      var plugin = window.Capacitor.Plugins.HardwarePtt;
      var pttDownListener = null;
      var pttUpListener = null;
      console.log('[PTT-DIAG] [JS] Setting up Capacitor HardwarePtt listeners');
      try {
        pttDownListener = plugin.addListener('pttDown', function() {
          console.log('[PTT-DIAG] [JS] Capacitor pttDown event received, keysLocked=' + keysLockedRef.current);
          if (keysLockedRef.current) return;
          if (!pttDown) {
            pttDown = true;
            console.log('[PTT-DIAG] [JS] Capacitor pttDown — calling handleTransmitStart');
            handleTransmitStartRef.current();
          }
        });
        pttUpListener = plugin.addListener('pttUp', function() {
          console.log('[PTT-DIAG] [JS] Capacitor pttUp event received');
          if (keysLockedRef.current) return;
          if (pttDown) {
            pttDown = false;
            console.log('[PTT-DIAG] [JS] Capacitor pttUp — calling handleTransmitEnd');
            handleTransmitEndRef.current();
          }
        });
      } catch(capErr) {
        console.warn('[T320] Capacitor PTT listener setup failed:', capErr);
      }
      capCleanup = function() {
        if (pttDownListener && pttDownListener.remove) pttDownListener.remove();
        if (pttUpListener && pttUpListener.remove) pttUpListener.remove();
      };
    }

    return function() {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keyup', onKeyUp, true);
      if (starLongPressTimer) clearTimeout(starLongPressTimer);
      clearEmergencyTimers();
      if (capCleanup) capCleanup();
    };
  }, []);

  useEffect(function() {
    function updateClock() {
      var now = new Date();
      var h = String(now.getHours()).padStart(2, '0');
      var m = String(now.getMinutes()).padStart(2, '0');
      setClockTime(h + ':' + m);
    }
    updateClock();
    var interval = setInterval(updateClock, 10000);
    return function() { clearInterval(interval); };
  }, []);

  useEffect(function() {
    if (navigator.getBattery) {
      navigator.getBattery().then(function(batt) {
        setBatteryLevel(Math.round(batt.level * 100));
        batt.addEventListener('levelchange', function() {
          setBatteryLevel(Math.round(batt.level * 100));
        });
      }).catch(function() {});
    }
  }, []);

  var displayZone = currentZone;
  var displayChannel = channelsLoading ? '---' : (currentChannel ? currentChannel.name : 'NO CH');
  var displayUnitId = null;

  if (isScanning && isReceiving && activeAudio) {
    var rxChannel = channels.find(function(ch) { return ch.name === activeAudio.channel; });
    if (rxChannel) {
      displayZone = rxChannel.zone || currentZone;
      displayChannel = rxChannel.name;
    }
    displayUnitId = activeAudio.from || transmittingUnitId;
  } else if (isReceiving && transmittingUnitId) {
    displayUnitId = transmittingUnitId;
  }

  if (isT320) {
    var t320Bg = '#ffffff';
    var t320Border = '#cccccc';
    var t320TextPrimary = '#111111';

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: isEmergency ? '#ff0000' : t320Bg,
        color: t320TextPrimary,
        fontFamily: "'Courier New', Courier, monospace",
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}>
        {isEmergency && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: '#ff0000',
            animation: 'emergFlash 0.5s infinite alternate',
            zIndex: 0,
          }} />
        )}

        <div style={{
          position: 'relative', zIndex: 1,
          display: 'flex', flexDirection: 'column',
          height: '100%',
        }}>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 8px',
            borderBottom: '1px solid ' + t320Border,
            fontSize: '11px',
            color: '#555555',
            backgroundColor: isEmergency ? 'transparent' : '#f0f0f0',
          }}>
            <span style={{ fontWeight: 'bold' }}>{clockTime}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {keysLocked && (
                <span style={{
                  color: '#cc8800',
                  fontWeight: 'bold',
                  animation: 'lockBlink 2s infinite',
                }}>LCK</span>
              )}
              {isScanning && (
                <span style={{ color: '#008844', fontWeight: 'bold' }}>SCN</span>
              )}
              <span style={{
                color: signalingConnected ? '#008844' : '#cc0000',
                fontWeight: 'bold',
              }}>
                {signalingConnected ? '●' : '○'}
              </span>
              {batteryLevel !== null && (
                <span style={{ fontWeight: 'bold' }}>
                  {batteryLevel + '%'}
                </span>
              )}
            </span>
          </div>

          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px 12px',
          }}>

            {emergencyHoldProgress && (
              <div style={{
                position: 'absolute', top: '30px', left: '10px', right: '10px',
                textAlign: 'center', zIndex: 10,
              }}>
                <div style={{
                  fontSize: '11px', fontWeight: 'bold',
                  color: emergencyHoldProgress.label === 'EMERG' ? '#cc0000' : '#008844',
                  marginBottom: '3px',
                  letterSpacing: '2px',
                }}>
                  {'HOLD... ' + emergencyHoldProgress.label + ' ' + emergencyHoldProgress.percent + '%'}
                </div>
                <div style={{
                  height: '4px', backgroundColor: '#ddd', borderRadius: '2px',
                }}>
                  <div style={{
                    height: '100%', borderRadius: '2px',
                    width: emergencyHoldProgress.percent + '%',
                    backgroundColor: emergencyHoldProgress.label === 'EMERG' ? '#cc0000' : '#008844',
                    transition: 'width 0.1s linear',
                  }} />
                </div>
              </div>
            )}

            {isTransmitting ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '36px', fontWeight: 'bold', color: '#cc0000',
                  letterSpacing: '6px', animation: 'txPulse 1s infinite',
                }}>TX</div>
              </div>
            ) : isEmergency && !isReceiving ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '24px', fontWeight: 'bold', color: '#ffffff',
                  letterSpacing: '4px', animation: 'emergText 0.3s infinite alternate',
                }}>EMERGENCY</div>
              </div>
            ) : (
              <>
                <div style={{
                  fontSize: '16px', fontWeight: 'bold',
                  color: isEmergency ? '#ffffff' : t320TextPrimary,
                  letterSpacing: '2px', textTransform: 'uppercase',
                  textAlign: 'center', marginBottom: '6px',
                }}>
                  {displayZone}
                </div>

                <div style={{
                  fontSize: '32px', fontWeight: 'bold',
                  color: isEmergency ? '#ffffff' : t320TextPrimary,
                  letterSpacing: '1px', lineHeight: '1.1',
                  textAlign: 'center',
                }}>
                  {displayChannel}
                </div>

                {displayUnitId && (
                  <div style={{
                    fontSize: '14px',
                    color: isEmergency ? '#ffffff' : '#006633',
                    fontWeight: 'bold', marginTop: '10px',
                    textAlign: 'center', letterSpacing: '1px',
                  }}>
                    {'ID: ' + displayUnitId}
                  </div>
                )}

                {isReceiving && !displayUnitId && (
                  <div style={{
                    fontSize: '12px', color: '#008844', fontWeight: 'bold',
                    marginTop: '8px', animation: 'rxPulse 1.5s infinite',
                    letterSpacing: '2px',
                  }}>RX</div>
                )}
              </>
            )}
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            backgroundColor: '#1a1a1a',
            padding: '10px 4px',
            borderTop: '2px solid #333333',
          }}>
            <button
              onClick={() => setShowT320Menu(showT320Menu === 'scan' ? null : 'scan')}
              style={{
                background: 'none', border: 'none', padding: '6px 12px',
                color: isScanning ? '#00ff77' : '#e0e0e0',
                fontSize: '14px', fontWeight: 'bold',
                letterSpacing: '1px', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >SCNL</button>
            <button
              onClick={() => setShowSettings(true)}
              style={{
                background: 'none', border: 'none', padding: '6px 12px',
                color: '#e0e0e0', fontSize: '14px', fontWeight: 'bold',
                letterSpacing: '1px', cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}
            ><Settings className="w-4 h-4" />SET</button>
            <button
              onClick={() => setShowT320Menu(showT320Menu === 'batt' ? null : 'batt')}
              style={{
                background: 'none', border: 'none', padding: '6px 12px',
                color: batteryLevel !== null && batteryLevel <= 20 ? '#ff3333' : '#ffffff',
                fontSize: '14px', fontWeight: 'bold',
                letterSpacing: '1px', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >{batteryLevel !== null ? batteryLevel + '%' : 'N/A'}</button>
          </div>
        </div>

        {showT320Menu === 'scan' && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: '#ffffff',
            fontFamily: "'Courier New', Courier, monospace",
            display: 'flex', flexDirection: 'column',
            zIndex: 100,
            color: '#111111',
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '6px 10px',
              borderBottom: '2px solid #333333',
              backgroundColor: '#f0f0f0',
            }}>
              <span style={{ fontSize: '13px', fontWeight: 'bold', letterSpacing: '2px' }}>SCAN LIST</span>
              <button
                onClick={toggleScanning}
                style={{
                  background: isScanning ? '#00cc66' : '#888888',
                  color: '#ffffff', border: 'none',
                  padding: '3px 10px', fontSize: '11px',
                  fontWeight: 'bold', letterSpacing: '1px',
                  fontFamily: 'inherit', cursor: 'pointer',
                }}
              >{isScanning ? 'ON' : 'OFF'}</button>
            </div>

            <div style={{
              flex: 1, overflowY: 'auto', padding: '4px 6px',
            }}>
              {scanChannels.map(function(ch) {
                return (
                  <div
                    key={ch.id}
                    onClick={function() { toggleScanChannel(ch.id); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 6px',
                      borderBottom: '1px solid #dddddd',
                      cursor: 'pointer',
                      backgroundColor: ch.enabled ? '#e6f7e6' : 'transparent',
                    }}
                  >
                    <span style={{
                      width: '18px', height: '18px',
                      border: '2px solid #333333',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '14px', fontWeight: 'bold',
                      backgroundColor: ch.enabled ? '#00aa44' : '#ffffff',
                      color: ch.enabled ? '#ffffff' : 'transparent',
                      flexShrink: 0,
                    }}>{ch.enabled ? '\u2713' : ''}</span>
                    <span style={{
                      fontSize: '13px', fontWeight: ch.enabled ? 'bold' : 'normal',
                      color: '#111111',
                    }}>{ch.name}</span>
                  </div>
                );
              })}
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-around', alignItems: 'center',
              backgroundColor: '#1a1a1a', padding: '6px 4px',
              borderTop: '2px solid #333333',
            }}>
              <button
                onClick={function() { setShowT320Menu(null); }}
                style={{
                  background: 'none', border: 'none', padding: '4px 8px',
                  color: '#00cc66', fontSize: '12px', fontWeight: 'bold',
                  letterSpacing: '1px', cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >BACK</button>
              <button
                onClick={function() {
                  scanChannels.forEach(function(ch) {
                    if (ch.enabled) toggleScanChannel(ch.id);
                  });
                }}
                style={{
                  background: 'none', border: 'none', padding: '4px 8px',
                  color: '#cccccc', fontSize: '12px', fontWeight: 'bold',
                  letterSpacing: '1px', cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >NONE</button>
            </div>
          </div>
        )}

        <style dangerouslySetInnerHTML={{ __html: '\
          @keyframes txPulse { 0%,100%{opacity:1} 50%{opacity:0.5} }\
          @keyframes rxPulse { 0%,100%{opacity:1} 50%{opacity:0.7} }\
          @keyframes emergFlash { 0%{opacity:1} 100%{opacity:0.4} }\
          @keyframes emergText { 0%{opacity:1} 100%{opacity:0.3} }\
          @keyframes lockBlink { 0%,100%{opacity:1} 50%{opacity:0.3} }\
        '}} />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen full-viewport w-full bg-gray-100 flex flex-col p-2 space-y-2 font-body"
      style={{
        paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)',
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
      }}
    >
      
      {clearAirActive && (
        <div className="w-full bg-blue-700 text-white text-center font-bold py-2 px-4 rounded-lg shadow-lg animate-pulse text-sm tracking-widest uppercase">
          CLEAR AIR — EMERGENCY TRAFFIC ONLY{clearAirChannelName ? ` (${clearAirChannelName})` : ''}
        </div>
      )}

      <div 
        className={cn(
          "bg-white rounded-lg p-3 shadow-sm border border-gray-200 cursor-pointer active:bg-gray-50 transition-all",
          statusLoading && "opacity-60",
          cadConnected === false && "border-red-300 bg-red-50"
        )}
        onClick={cadConnected === false ? undefined : handleCycleStatus}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500 font-medium mb-1">
              My Status {hasActiveCall && <span className="text-orange-600">(CALL ACTIVE)</span>}
            </div>
            <div className={cn("text-xl font-bold", cadConnected === false ? "text-red-600" : "text-black")}>
              {statusLoading ? "..." : cadConnected === false ? "CAD DISCONNECTED" : formatStatus(unitStatus)}
            </div>
          </div>
          <div className="text-xs text-gray-400">{cadConnected === false ? "No CAD connection" : "Tap to cycle"}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
        <div className="flex items-stretch gap-2">
          <div className="flex-1 flex flex-col">
            <div className="text-xs text-gray-500 font-medium">{currentZone}</div>
            <div className="text-2xl font-bold text-black">
              {channelsLoading ? "LOADING..." : currentChannel?.name || "NO CHANNEL"}
            </div>
            
            {isReceiving && transmittingUnitId && (
              <div className="mt-1 text-lg font-medium text-cyan-600 animate-pulse">
                RX: {transmittingUnitId}
              </div>
            )}
            
            <div className="flex items-center gap-2 text-xs mt-auto">
              {isScanning && <span className="text-green-600 font-bold">SCAN</span>}
              {isEmergency && <span className="text-red-600 font-bold animate-pulse">EMERG</span>}
              {cadConnected ? (
                <span className="text-green-600 flex items-center gap-0.5">
                  <Wifi className="w-3 h-3" />CAD
                </span>
              ) : (
                <span className="text-red-500 flex items-center gap-0.5">
                  <WifiOff className="w-3 h-3" />CAD
                </span>
              )}
            </div>
          </div>

          <button
            onClick={toggleScanning}
            className={cn(
              "w-8 h-8 rounded border flex items-center justify-center active:scale-95 transition-all self-center",
              isScanning 
                ? "bg-green-500 border-green-600" 
                : "bg-gray-100 border-gray-300"
            )}
          >
            <svg 
              viewBox="0 0 24 24" 
              className={cn("w-6 h-6", isScanning ? "text-white" : "text-gray-700")}
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M6 3 L3 6 L6 9" />
              <path d="M7 6 H17 L7 18 H17" />
              <path d="M18 15 L21 18 L18 21" />
            </svg>
          </button>

          <div className="flex flex-col items-center gap-1">
            <button
              onClick={handleZoneUp}
              className="w-10 h-10 bg-gray-100 rounded border border-gray-300 flex items-center justify-center active:bg-gray-200 active:scale-95 transition-all"
            >
              <ChevronUp className="w-6 h-6 text-black" />
            </button>
            <div className="text-xs text-gray-500 font-mono">ZN</div>
            <button
              onClick={handleZoneDown}
              className="w-10 h-10 bg-gray-100 rounded border border-gray-300 flex items-center justify-center active:bg-gray-200 active:scale-95 transition-all"
            >
              <ChevronDown className="w-6 h-6 text-black" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-1">
            <button
              onClick={handleChannelUp}
              className="w-10 h-10 bg-gray-100 rounded border border-gray-300 flex items-center justify-center active:bg-gray-200 active:scale-95 transition-all"
            >
              <ChevronUp className="w-6 h-6 text-black" />
            </button>
            <div className="text-xs text-gray-500 font-mono">CH</div>
            <button
              onClick={handleChannelDown}
              className="w-10 h-10 bg-gray-100 rounded border border-gray-300 flex items-center justify-center active:bg-gray-200 active:scale-95 transition-all"
            >
              <ChevronDown className="w-6 h-6 text-black" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setShowScanList(true)}
          className={cn(
            "flex-1 bg-white rounded-lg p-3 shadow-sm border border-gray-200 flex flex-col items-center gap-1 active:bg-gray-50 transition-all",
            isScanning && "bg-green-50 border-green-300"
          )}
        >
          <List className="w-6 h-6 text-cyan-600" />
          <span className="text-xs text-gray-600 font-medium">Scan Lists</span>
        </button>
        
        <button
          onClick={handleOpenContacts}
          className="flex-1 bg-white rounded-lg p-3 shadow-sm border border-gray-200 flex flex-col items-center gap-1 active:bg-gray-50 transition-all"
        >
          <Users className="w-6 h-6 text-cyan-600" />
          <span className="text-xs text-gray-600 font-medium">Contacts</span>
        </button>
        
        <button
          onClick={() => setShowSettings(true)}
          className="flex-1 bg-white rounded-lg p-3 shadow-sm border border-gray-200 flex flex-col items-center gap-1 active:bg-gray-50 transition-all"
        >
          <MoreHorizontal className="w-6 h-6 text-cyan-600" />
          <span className="text-xs text-gray-600 font-medium">More</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-cyan-600" />
            <span className="font-bold text-black">Messages</span>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold ml-1">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'No new messages'}
          </div>
        </div>
        <div className="flex border-t border-gray-100">
          <button 
            onClick={() => setShowNewMessage(true)}
            className="flex-1 py-3 flex items-center justify-center gap-2 text-cyan-600 font-medium text-sm border-r border-gray-100 active:bg-gray-50"
          >
            <Plus className="w-4 h-4" />
            New Conversation
          </button>
          <button 
            onClick={() => setShowMessages(true)}
            className="flex-1 py-3 flex items-center justify-center gap-2 text-cyan-600 font-medium text-sm active:bg-gray-50"
          >
            <Mail className="w-4 h-4" />
            All Messages
          </button>
        </div>
      </div>

      <button
        onClick={() => isEmergency ? handleEmergencyDeactivate() : handleEmergencyActivate()}
        className={cn(
          "w-full rounded-lg p-3 flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wider transition-all active:scale-98",
          isEmergency 
            ? "bg-red-600 text-white animate-pulse" 
            : "bg-white border border-gray-200 text-red-600 shadow-sm"
        )}
      >
        <AlertTriangle className="w-5 h-5" />
        {isEmergency ? "EMERGENCY ACTIVE" : "EMERGENCY"}
      </button>

      {showPTT && (
        <PTTButton
          onTransmitStart={handleTransmitStart}
          onTransmitEnd={handleTransmitEnd}
          isConnected={signalingConnected}
          isTransmitting={isTransmitting}
          isReceiving={isReceiving}
          activeSpeaker={transmittingUnitId}
          setTransmitting={setIsTransmitting}
        />
      )}

      <div className="grid grid-cols-2 gap-2 flex-1 auto-rows-[4rem]">
        {functionButtons.map((btn) => {
          const display = getButtonDisplay(btn.id);
          return (
            <div
              key={btn.id}
              draggable
              onDragStart={() => handleDragStart(btn.id)}
              onDragOver={(e) => handleDragOver(e, btn.id)}
              onDragEnd={handleDragEnd}
              onClick={() => handleFunctionButtonClick(btn.id)}
              className={cn(
                "bg-white rounded-xl border border-gray-200 flex items-center justify-center text-gray-700 shadow-sm active:bg-gray-50 transition-all cursor-pointer relative",
                btn.id === 'widget' ? "col-span-2 row-span-2 flex-1" : "h-16",
                draggedItem === btn.id && "opacity-50 scale-95"
              )}
            >
              <GripVertical className="w-4 h-4 text-gray-300 absolute left-2" />
              <div className="flex flex-col items-center">
                <span className="font-bold text-sm">{display.label}</span>
                {display.sublabel && <span className="text-xs text-gray-500">{display.sublabel}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {showScanList && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border-2 border-black w-full max-w-sm max-h-[70vh] flex flex-col">
            <div className="p-4 border-b border-black flex items-center justify-between">
              <h2 className="text-black font-mono font-bold uppercase tracking-wider">Scan List</h2>
              <button
                onClick={toggleScanning}
                className={cn(
                  "px-4 py-2 rounded font-mono text-xs uppercase border border-black",
                  isScanning ? "bg-green-300 text-black font-bold" : "bg-gray-200 text-black"
                )}
              >
                {isScanning ? 'SCANNING' : 'OFF'}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {scanChannels.map((ch) => (
                <label key={ch.id} className="flex items-center gap-3 p-2 border border-gray-200 rounded">
                  <input
                    type="checkbox"
                    checked={ch.enabled}
                    onChange={() => toggleScanChannel(ch.id)}
                    className="w-5 h-5"
                  />
                  <span className="font-mono text-black">{ch.name}</span>
                </label>
              ))}
            </div>
            <div className="p-4 border-t border-black">
              <button
                onClick={() => setShowScanList(false)}
                className="w-full py-2 bg-black text-white font-mono uppercase rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-zinc-900 w-full h-full flex flex-col overflow-hidden">
            <div className="p-4 border-b border-zinc-700 flex items-center justify-between bg-zinc-800">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-cyan-400" />
                <h2 className="text-white font-mono font-bold uppercase tracking-wider text-sm">Settings</h2>
              </div>
              <button onClick={() => { setShowSettings(false); setCapturingPttKey(false); }} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {!appSettings ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                </div>
              ) : (
                <>
                  <section>
                    <h3 className="text-[11px] font-bold text-zinc-500 uppercase tracking-widest px-1 mb-2">Audio</h3>
                    <div className="bg-zinc-800/60 rounded-xl border border-zinc-700/50 overflow-hidden">
                      <div className="p-3 border-b border-zinc-700/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Volume2 size={16} className="text-zinc-400" />
                          <span className="text-sm text-zinc-200">Incoming Volume</span>
                          <span className="text-xs text-cyan-400 ml-auto">{appSettings.incomingVolume}%</span>
                        </div>
                        <input type="range" min="0" max="100" value={appSettings.incomingVolume}
                          onChange={(e) => updateAppSetting('incomingVolume', parseInt(e.target.value))}
                          className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                        />
                      </div>

                      <div className="p-3 border-b border-zinc-700/30">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Volume2 size={16} className="text-zinc-400" />
                            <span className="text-sm text-zinc-200">Auto-increase after inactivity</span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={appSettings.autoIncreaseVolumeEnabled}
                              onChange={(e) => updateAppSetting('autoIncreaseVolumeEnabled', e.target.checked)}
                              className="sr-only peer" />
                            <div className="w-9 h-5 bg-zinc-600 peer-checked:bg-cyan-500 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                          </label>
                        </div>
                        <p className="text-[10px] text-zinc-500 mb-1">After 10 min of no incoming messages, increase to set level</p>
                        {appSettings.autoIncreaseVolumeEnabled && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-zinc-400">Level:</span>
                            <input type="range" min="50" max="100" value={appSettings.autoIncreaseVolumeLevel}
                              onChange={(e) => updateAppSetting('autoIncreaseVolumeLevel', parseInt(e.target.value))}
                              className="flex-1 h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                            />
                            <span className="text-xs text-cyan-400 w-8 text-right">{appSettings.autoIncreaseVolumeLevel}%</span>
                          </div>
                        )}
                      </div>

                      <div className="p-3 border-b border-zinc-700/30">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Volume2 size={16} className="text-zinc-400" />
                            <span className="text-sm text-zinc-200">Playback Amplifier</span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={appSettings.playbackAmplifierEnabled}
                              onChange={(e) => updateAppSetting('playbackAmplifierEnabled', e.target.checked)}
                              className="sr-only peer" />
                            <div className="w-9 h-5 bg-zinc-600 peer-checked:bg-cyan-500 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                          </label>
                        </div>
                        <p className="text-[10px] text-zinc-500">Software gain boost on incoming audio</p>
                        {appSettings.playbackAmplifierEnabled && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-zinc-400">Gain:</span>
                            <input type="range" min="0" max="100" value={appSettings.playbackAmplifierLevel}
                              onChange={(e) => updateAppSetting('playbackAmplifierLevel', parseInt(e.target.value))}
                              className="flex-1 h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                            />
                            <span className="text-xs text-cyan-400 w-8 text-right">{appSettings.playbackAmplifierLevel}%</span>
                          </div>
                        )}
                      </div>

                      <div className="p-3 border-b border-zinc-700/30">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Mic size={16} className="text-zinc-400" />
                            <span className="text-sm text-zinc-200">Recording Amplifier</span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={appSettings.recordingAmplifierEnabled}
                              onChange={(e) => updateAppSetting('recordingAmplifierEnabled', e.target.checked)}
                              className="sr-only peer" />
                            <div className="w-9 h-5 bg-zinc-600 peer-checked:bg-cyan-500 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                          </label>
                        </div>
                        <p className="text-[10px] text-zinc-500">Software gain boost on outgoing mic audio</p>
                        {appSettings.recordingAmplifierEnabled && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-zinc-400">Gain:</span>
                            <input type="range" min="0" max="100" value={appSettings.recordingAmplifierLevel}
                              onChange={(e) => updateAppSetting('recordingAmplifierLevel', parseInt(e.target.value))}
                              className="flex-1 h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                            />
                            <span className="text-xs text-cyan-400 w-8 text-right">{appSettings.recordingAmplifierLevel}%</span>
                          </div>
                        )}
                      </div>

                      <SettingsToggleItem
                        icon={<Mic size={16} />}
                        title="Noise Suppression"
                        description="Reduce background noise on outgoing audio"
                        checked={appSettings.noiseSuppressionEnabled}
                        onChange={(v) => updateAppSetting('noiseSuppressionEnabled', v)}
                      />

                      <SettingsToggleItem
                        icon={<Bluetooth size={16} />}
                        title="Audio mode only on send/receive"
                        description="Activate BT SCO/speakerphone only during TX/RX"
                        checked={appSettings.audioModeOnSendReceiveOnly}
                        onChange={(v) => updateAppSetting('audioModeOnSendReceiveOnly', v)}
                      />
                    </div>
                  </section>

                  <section>
                    <h3 className="text-[11px] font-bold text-zinc-500 uppercase tracking-widest px-1 mb-2">PTT Button</h3>
                    <div className="bg-zinc-800/60 rounded-xl border border-zinc-700/50 overflow-hidden">
                      <div
                        className="flex items-center justify-between p-3 border-b border-zinc-700/30 cursor-pointer hover:bg-zinc-700/30 active:bg-zinc-700/50"
                        onClick={() => setCapturingPttKey(true)}
                      >
                        <div className="flex items-center gap-2">
                          <Radio size={16} className="text-zinc-400" />
                          <div>
                            <p className="text-sm text-zinc-200">Hardware PTT Key</p>
                            <p className="text-[10px] text-zinc-500">
                              {capturingPttKey ? 'Press any key now...' : `Current: ${appSettings.pttKeyLabel}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={cn("text-xs font-bold", capturingPttKey ? 'text-yellow-400 animate-pulse' : 'text-cyan-400')}>
                            {capturingPttKey ? 'LISTENING' : 'SET'}
                          </span>
                          <ChevronRight size={14} className="text-zinc-600" />
                        </div>
                      </div>

                      <SettingsToggleItem
                        icon={<Volume2 size={16} />}
                        title="Volume Button PTT"
                        description="Use volume button as Push-to-Talk"
                        checked={appSettings.volumeButtonPtt}
                        onChange={async (v) => {
                          const newSettings = { ...appSettings, volumeButtonPtt: v };
                          if (v) {
                            newSettings.pttKeyLabel = 'VolumeUp';
                            newSettings.pttKeyCode = 24;
                          } else if (appSettings.pttKeyLabel === 'VolumeUp') {
                            newSettings.pttKeyLabel = 'Screen Button';
                            newSettings.pttKeyCode = null;
                          }
                          setAppSettings(newSettings);
                          await saveSettings(newSettings);
                        }}
                      />

                      <SettingsToggleItem
                        icon={<Bluetooth size={16} />}
                        title="Bluetooth Headset Media Button"
                        description="Use BT headset media button for PTT"
                        checked={appSettings.bluetoothMediaButtonPtt}
                        onChange={(v) => updateAppSetting('bluetoothMediaButtonPtt', v)}
                      />
                    </div>
                  </section>

                  <section>
                    <h3 className="text-[11px] font-bold text-zinc-500 uppercase tracking-widest px-1 mb-2">Behavior</h3>
                    <div className="bg-zinc-800/60 rounded-xl border border-zinc-700/50 overflow-hidden">
                      <SettingsToggleItem
                        icon={<Power size={16} />}
                        title="Start App on Boot"
                        description="Launch background service when device boots"
                        checked={appSettings.startOnBoot}
                        onChange={(v) => updateAppSetting('startOnBoot', v)}
                      />

                      <div className="p-3 border-b border-zinc-700/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Monitor size={16} className="text-zinc-400" />
                            <div>
                              <p className="text-sm text-zinc-200">Bring App to Foreground</p>
                              <p className="text-[10px] text-zinc-500">When to bring app to front on incoming message</p>
                            </div>
                          </div>
                        </div>
                        <select
                          value={appSettings.foregroundOnMessage}
                          onChange={(e) => updateAppSetting('foregroundOnMessage', e.target.value)}
                          className="mt-2 w-full bg-zinc-700 border border-zinc-600 rounded px-2 py-1.5 text-sm text-zinc-200"
                        >
                          <option value="never">Never</option>
                          <option value="screen_off">When screen is off</option>
                          <option value="always">When any message is received</option>
                        </select>
                      </div>

                      <SettingsToggleItem
                        icon={<Bell size={16} />}
                        title="Push Notifications"
                        description="Show notifications for incoming radio traffic"
                        checked={appSettings.pushNotificationsEnabled}
                        onChange={(v) => updateAppSetting('pushNotificationsEnabled', v)}
                      />

                      <SettingsToggleItem
                        icon={<Mic size={16} />}
                        title="Start on Incoming Voice"
                        description="Bring app to foreground on incoming voice message"
                        checked={appSettings.startOnVoiceMessage}
                        onChange={(v) => updateAppSetting('startOnVoiceMessage', v)}
                      />

                      <SettingsToggleItem
                        icon={<BatteryCharging size={16} />}
                        title="Working in Background"
                        description={
                          !isNative ? "Keep service active in background"
                            : batteryOptExempt === true ? "Battery optimization exempt"
                            : batteryOptExempt === false ? "Not exempt — tap to request"
                            : "Request battery optimization exemption"
                        }
                        checked={appSettings.workingInBackground}
                        onChange={async (v) => {
                          if (v && isNative && window.Capacitor?.Plugins?.BackgroundService) {
                            try {
                              await window.Capacitor.Plugins.BackgroundService.requestBatteryOptimizationExemption();
                              const r = await window.Capacitor.Plugins.BackgroundService.checkBatteryOptimization();
                              setBatteryOptExempt(r?.isExempt ?? null);
                            } catch (e) {
                              console.warn('Battery optimization request failed:', e);
                            }
                          }
                          updateAppSetting('workingInBackground', v);
                        }}
                      />

                      <SettingsToggleItem
                        icon={<Moon size={16} />}
                        title="Wake Up Device"
                        description="Hold CPU wake lock to prevent sleep"
                        checked={appSettings.wakeDevice}
                        onChange={(v) => updateAppSetting('wakeDevice', v)}
                      />
                    </div>
                  </section>

                  <section>
                    <h3 className="text-[11px] font-bold text-zinc-500 uppercase tracking-widest px-1 mb-2">UI Configuration</h3>
                    <div className="bg-zinc-800/60 rounded-xl border border-zinc-700/50 overflow-hidden">
                      <SettingsToggleItem
                        icon={<Smartphone size={16} />}
                        title="Show PTT Button"
                        description="Show on-screen Push-to-Talk button"
                        checked={showPTT}
                        onChange={togglePTTVisibility}
                      />

                      <div className="p-3 border-b border-zinc-700/30 space-y-2">
                        <p className="text-sm text-zinc-200">Widget Configuration</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-400">F3 Button</span>
                          <select value={widgetConfig.f3} onChange={(e) => handleWidgetConfigChange('f3', e.target.value)}
                            className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200">
                            {F3F4_WIDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-400">F4 Button</span>
                          <select value={widgetConfig.f4} onChange={(e) => handleWidgetConfigChange('f4', e.target.value)}
                            className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200">
                            {F3F4_WIDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-400">Large Widget</span>
                          <select value={widgetConfig.widget} onChange={(e) => handleWidgetConfigChange('widget', e.target.value)}
                            className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-zinc-200">
                            {MAIN_WIDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </section>

                  <button
                    onClick={() => {
                      setShowSettings(false);
                      if (onLogout) onLogout();
                    }}
                    className="w-full py-3 border border-red-800/50 text-red-500 hover:bg-red-950/30 rounded-lg font-bold tracking-widest uppercase text-sm"
                  >
                    Disconnect & Logout
                  </button>
                  <div className="h-4" />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showContacts && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border-2 border-black w-full max-w-sm max-h-[70vh] flex flex-col">
            <div className="p-4 border-b border-black flex items-center justify-between">
              <h2 className="text-black font-mono font-bold uppercase tracking-wider">Contacts</h2>
              <button onClick={() => setShowContacts(false)} className="text-black">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {contactsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : contacts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No contacts found</p>
              ) : (
                <div className="space-y-2">
                  {contacts.map((contact, i) => (
                    <div key={i} className="p-3 border border-gray-200 rounded">
                      <p className="font-bold text-black">{contact.name || contact.unit_id}</p>
                      {contact.role && <p className="text-xs text-gray-500">{contact.role}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPersonQuery && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border-2 border-black w-full max-w-sm flex flex-col">
            <div className="p-4 border-b border-black flex items-center justify-between">
              <h2 className="text-black font-mono font-bold uppercase tracking-wider">Person Query</h2>
              <button onClick={() => setShowPersonQuery(false)} className="text-black">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <input
                type="text"
                placeholder="First Name"
                value={personQueryForm.firstName}
                onChange={(e) => setPersonQueryForm(p => ({ ...p, firstName: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded text-black"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={personQueryForm.lastName}
                onChange={(e) => setPersonQueryForm(p => ({ ...p, lastName: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded text-black"
              />
              <input
                type="text"
                placeholder="DOB (MM/DD/YYYY)"
                value={personQueryForm.dob}
                onChange={(e) => setPersonQueryForm(p => ({ ...p, dob: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded text-black"
              />
              {queryError && <p className="text-red-600 text-sm">{queryError}</p>}
              {queryResult && (
                <div className="p-3 bg-gray-100 rounded text-sm text-black">
                  <pre className="whitespace-pre-wrap">{JSON.stringify(queryResult, null, 2)}</pre>
                </div>
              )}
              <button
                onClick={handlePersonQuery}
                disabled={queryLoading}
                className="w-full py-2 bg-cyan-600 text-white font-bold uppercase rounded flex items-center justify-center gap-2"
              >
                {queryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </button>
            </div>
          </div>
        </div>
      )}

      {showVehicleQuery && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border-2 border-black w-full max-w-sm flex flex-col">
            <div className="p-4 border-b border-black flex items-center justify-between">
              <h2 className="text-black font-mono font-bold uppercase tracking-wider">Vehicle Query</h2>
              <button onClick={() => setShowVehicleQuery(false)} className="text-black">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <input
                type="text"
                placeholder="Plate Number"
                value={vehicleQueryForm.plate}
                onChange={(e) => setVehicleQueryForm(p => ({ ...p, plate: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded text-black"
              />
              <input
                type="text"
                placeholder="State (e.g., IN)"
                value={vehicleQueryForm.state}
                onChange={(e) => setVehicleQueryForm(p => ({ ...p, state: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded text-black"
              />
              <input
                type="text"
                placeholder="VIN (optional)"
                value={vehicleQueryForm.vin}
                onChange={(e) => setVehicleQueryForm(p => ({ ...p, vin: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded text-black"
              />
              {queryError && <p className="text-red-600 text-sm">{queryError}</p>}
              {queryResult && (
                <div className="p-3 bg-gray-100 rounded text-sm text-black">
                  <pre className="whitespace-pre-wrap">{JSON.stringify(queryResult, null, 2)}</pre>
                </div>
              )}
              <button
                onClick={handleVehicleQuery}
                disabled={queryLoading}
                className="w-full py-2 bg-cyan-600 text-white font-bold uppercase rounded flex items-center justify-center gap-2"
              >
                {queryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </button>
            </div>
          </div>
        </div>
      )}

      <AnimalSearchModal show={showAnimalSearch} onClose={() => setShowAnimalSearch(false)} />
      <CitationModal show={showCitation} onClose={() => setShowCitation(false)} />
      <CadMapModal show={showCadMap} onClose={() => setShowCadMap(false)} />
      <FieldInterviewModal show={showFI} onClose={() => setShowFI(false)} />
      <FleetModal show={showFleet} onClose={() => setShowFleet(false)} identity={identity} />
      <WeatherWidget show={showWeather} onClose={() => setShowWeather(false)} />
      <BoloWidget show={showBolo} onClose={() => setShowBolo(false)} />
      <MessagesWidget show={showMessages} onClose={() => setShowMessages(false)} />
      <NewMessageModal show={showNewMessage} onClose={() => setShowNewMessage(false)} />
    </div>
  );
}

function SettingsToggleItem({ icon, title, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 border-b border-zinc-700/30 last:border-0">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="text-zinc-400 flex-shrink-0">{icon}</div>
        <div className="min-w-0">
          <p className="text-sm text-zinc-200">{title}</p>
          <p className="text-[10px] text-zinc-500">{description}</p>
        </div>
      </div>
      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-2">
        <input type="checkbox" checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer" />
        <div className="w-9 h-5 bg-zinc-600 peer-checked:bg-cyan-500 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
      </label>
    </div>
  );
}
